import {isPersonalizationRoute} from "../account/brand/personalizationRoute";
import {RECIPIENT_PATH_PATTERN} from "../attribution/adAttribution";
import {isFeaturePanelRoute, isHomeRoute} from "../featurePanelRoutes";
import {treeRelativePath} from "../seo/documentLang";

/**
 * Which routes carry the shell's legal footer row (kitecorp/transferify-ui#2394). React-free by
 * mandate (epic #26), so the rule can be asserted directly instead of through a rendered shell.
 *
 * <p>The answer is "the STOREFRONT routes, and only those": the marketing, content and legal pages a
 * consumer reads. Every WORKING screen is excluded — the surfaces somebody is using to do something
 * rather than to read about it — and each exception below is deliberate rather than a convenience.
 *
 * <p><b>The rule the exceptions share</b> (owner instruction 2026-09-20): "TAKE THE FOOTER OFF SEND
 * SUCCESS SCREENS TOO THE ONLY ALLOWED TEXT LIKE THAT IS IF IT'S HIDDEN and takes no space." Legal and
 * navigational text must not occupy vertical space on a working screen. It may exist there only where
 * it costs none — the header's Resources disclosure, whose rows are laid out but `visibility: hidden`
 * until it is opened — which is where the § 312k cancellation control stays reachable on every route
 * named here.
 *
 * <ul>
 *   <li>The LANDING carries no row at all (owner instruction 2026-09-19): "Anulează contractele /
 *       Termeni / Confidențialitate / Informații legale remove these from landing page... we already
 *       have them under resources menu. AND STOP ADDING STUFF ON THE LANDING PAGE LIKE THESE. LANDING
 *       PAGE NEEDS TO LOOK GOOD AND THESE BREAK THAT." It is also the page with the least room to give:
 *       `home/landing/campaignSpace` budgets the promotion against the SURFACE's end, and the row was
 *       taking 65px off it — 115px at 320px wide, where it wraps to two touch-target rows — which is
 *       what ui#2645, ui#2650 and ui#2635 are all downstream of. The SEND flow and its SUCCESS card are
 *       this same surface — `home/CreateFlow` inside `home/HomePage` — so they are answered for here
 *       rather than by a rule of their own.</li>
 *   <li>The PRIVATE DESTINATIONS — the docked FeaturePanel routes and the Personalization sheet — are
 *       working screens by the same reading, and they are the half ui#2681 could not reach. They are
 *       also signed-in only: `PrivateRoute` redirects a logged-out visitor to "/", so none of them is a
 *       page a consumer arrives at looking for the cancellation control, and none carries storefront
 *       content the row could be the reachable copy of. Both open OVER the persistent home canvas, so
 *       leaving the row on them cost more than its own height: the create card's `max-height` subtracts
 *       `--site-footer-reserve`, which made the sender's own drop surface 33px shorter on `/transfers`
 *       than on `/` — measured by `e2e/client-only-route-shell.spec.ts`, whose whole subject is that
 *       this surface does not move when a destination opens over it.</li>
 *   <li>`/download/:id` and `/reminder-uploads/:reminderId/:contactId` are pinned to the viewport and
 *       must not scroll (owner 2026-09-15, ui#687 and ui#2457: "the recipient/upload page should be
 *       fixed not scroll with the page"). A footer band would take 65px off a surface that was tuned
 *       down to 375x667 and that a sender can PAY to decorate.</li>
 *   <li>They are also the pages `consent/consentClearance` mints NO room for, by contract — a
 *       `[data-viewport-locked]` surface lays itself out around the consent bar instead. A footer row
 *       placed there would come to rest exactly under that floating bar with no gesture able to free
 *       it, which is worse than not offering the control twice.</li>
 *   <li>Neither delivery route exists in the `/ro` or `/de` SSG trees at all (`routes.tsx`): they are
 *       delivery surfaces, served on a customer's own claimed host as often as on ours, not storefront
 *       pages where a consumer goes looking for the cancellation control.</li>
 * </ul>
 *
 * <p>NO exception is restated here, and that is what keeps this rule honest as the app grows. The
 * recipient family is `attribution/adAttribution`'s list — its pattern is anchored and segment-bounded,
 * so `/downloads-for-teams` is a storefront page rather than a download. The landing is
 * {@link isHomeRoute}, the panel routes are {@link isFeaturePanelRoute} and the Personalization sheet is
 * {@link isPersonalizationRoute} — the SAME three predicates `App` computes `showHomeCanvas` from, so a
 * surface the shell treats as a working screen and a surface this keeps the row off cannot become two
 * different sets. A destination added to `featurePanelRoutes` is answered for on the day it is added,
 * with no edit here. `scripts/working-screen-legal-row-sweep.sh` fails if any of them is dropped or
 * replaced by a hand-written path, because a second copy of either list is exactly how two answers
 * drift: a hand-written `pathname === "/"` would have taken the row off the English landing and left it
 * on the Romanian and German ones, which is ui#2501's defect with the sign flipped.
 *
 * <p>The landing and delivery tests read the TREE-RELATIVE path, via {@link treeRelativePath} here and
 * inside {@link isHomeRoute}, rather than relying on the published pattern's own optional `ro/` —
 * `seo/documentLang` already owns what `/ro` and `/de` mean, and it matches React Router's
 * case-insensitivity, which a hand-written prefix regex here would not. That is what makes the landing
 * exception hold at all four of its addresses: the bare root and the `/ro` and `/de` mirrors on the main
 * domain (ui#2471), and the root again on each brand host. For the delivery surfaces the difference is
 * invisible today, since no locale tree serves one; that is the point: the day one does, this answers
 * about the route instead of silently putting a footer band back on a viewport-locked page under one
 * language only.
 *
 * <p>The two private predicates read the RAW pathname instead, which is not an oversight: they answer
 * about client-only routes that exist in the root tree alone, and `App` docks the panel from the raw
 * pathname too ({@link ../featurePanelRoutes.featureSheetIdFor}). Collapsing the mirror first would make
 * this rule claim `/de/account` — a path the shell would render as an ordinary page with no panel on it
 * — and the two answers would disagree about the one thing they exist to agree on.
 */
export function showsSiteFooter(pathname: string): boolean {
	return !isHomeRoute(pathname)
		&& !isFeaturePanelRoute(pathname)
		&& !isPersonalizationRoute(pathname)
		&& !RECIPIENT_PATH_PATTERN.test(treeRelativePath(pathname));
}
